import {
  normalizer,
  get,
  getRequest
} from 'helpers/RequestUtil'
import { delay } from 'redux-saga'
import {
  select,
  fork,
  put,
  all,
  call,
  take,
  takeLatest,
  takeEvery,
  cancelled,
} from 'redux-saga/effects'
import { baseURL } from 'config'
import * as R from 'ramda'
import P from 'helpers/predicates'

import {
  updateTablesAction,
  removeTables
} from 'containers/Entity/actions'

import {
  MOUNT,
  ON_INPUT_CHANGE,
  ON_ENTER,
  ON_SKU_CHANGE,
  CLEAR_SKU,
  CLEAR_SEARCH,
  UPDATE_SKU,
  UPDATE_SEARCH,
  FETCH_PRODUCT_SKUS,
  FETCH_GREENLIGHT_REPORT,
} from './constants'

import {
  clearSku,
  clearSearch,
  clearTableFilter,
  clearTableSort,
  updateSku,
  updateTableFilter,
  updateTableSort,
  updateSearch,
  fetchProductSkusStart,
  fetchProductSkusSuccess,
  fetchProductSkusError,
  startFetchProductSkusAsync,
  fetchGreenlightReport as fetchGreenlightReportAction,
  fetchGreenlightReportStart,
  fetchGreenlightReportSuccess,
  fetchGreenlightReportError,
  startFetchGreenlightReport,
} from './actions'

import {
  skuSelector,
  searchSelector
} from './selectors'

import { clearData } from 'containers/Project/actions'

function* fetchGreenlightReport(sku, search) {
  try {
    yield put(startFetchGreenlightReport())
    yield put(fetchGreenlightReportStart())
    const query = `?product_sku=${sku}`
    const data = yield call(getRequest, `${baseURL}/api/fts/reports${query}`)
    const normalized = normalizer(data, 'greenlight_report', 'project')
    yield put(updateTablesAction(normalized))
    yield put(fetchGreenlightReportSuccess())
    return normalized
  } catch (error) {
    yield put(fetchGreenlightReportError(error))
  }
}

function* fetchProductSkus(parent = 1, depth = 5) {
  try {
    yield put(startFetchProductSkusAsync())
    yield put(fetchProductSkusStart())
    const data = yield call(get, `/api/fulcrum/product_hierarchy_views/${parent}/children?filter[depth]=${depth}`)
    yield put(fetchProductSkusSuccess())
    yield put(updateTablesAction(data))
  } catch (error) {
    yield put(fetchProductSkusError(error))
  }
}

function* handleFetchProductSkus() {
  yield delay(500)
  yield call(fetchProductSkus)
}

function* handleFetchGreenlightReport({ sku, query }) {
  yield delay(500)
  yield put(removeTables('greenlight_report'))
  yield call(fetchGreenlightReport, sku, query)
}

function* handleInputChange(action) {
  const { value } = action
  yield put(updateSearch(value))
}

function* fetchProject() {
  const search = yield select(searchSelector)
  const sku = yield select(skuSelector)
  if (P.isNotEmpty(sku))
    yield put(fetchGreenlightReportAction(sku, search))
}

function* handleSkuChange(action) {
  const { value } = action
  yield put(updateSku(value))
  yield put(clearTableFilter())
  yield put(clearTableSort())
  yield fork(fetchProject)
}

function* handleMount({ sku, tableFilter, tableSort }) {
  yield put(clearData())
  yield put(clearSku())
  yield put(clearSearch())
  yield put(clearTableFilter())
  yield put(clearTableSort())
  yield put(removeTables('greenlight_report'))
  yield fork(handleFetchProductSkus)
  if (sku) {
    yield put(updateSku(sku))
    yield put(fetchGreenlightReportAction(sku))
  }
  yield put(updateTableFilter(tableFilter))
  yield put(updateTableSort(tableSort))
}

function* watchEnter() {
  yield takeLatest(ON_ENTER, fetchProject)
}

function* watchInputChange() {
  yield takeEvery(ON_INPUT_CHANGE, handleInputChange)
}

function* watchSkuChange() {
  yield takeEvery(ON_SKU_CHANGE, handleSkuChange)
}

function* watchFetchProductSkus() {
  yield takeLatest(FETCH_PRODUCT_SKUS, handleFetchProductSkus)
}

function* watchFetchGreenlightReport() {
  yield takeLatest(FETCH_GREENLIGHT_REPORT, handleFetchGreenlightReport)
}

function* watchMount() {
  yield takeEvery(MOUNT, handleMount)
}

function* loadSagas() {
  yield all([
    fork(watchEnter),
    fork(watchInputChange),
    fork(watchSkuChange),
    fork(watchFetchProductSkus),
    fork(watchFetchGreenlightReport)
  ])
}

export { watchMount }
export default loadSagas
